import { BuilderRef, QueryData } from '@/types';
import type { BuilderElement } from '@builder.io/sdk';

export const findBlockByComponentName = (
	blocks: BuilderElement[] | undefined,
	name: string | string[],
): BuilderElement | undefined => {
	if (!blocks) return undefined;

	const names = Array.isArray(name) ? name : [name];

	for (const block of blocks) {
		if (block.component?.name && names.includes(block.component.name)) return block;
		if (block.children) {
			const found = findBlockByComponentName(block.children, names);
			if (found) return found;
		}
	}

	return undefined;
};

// Extract ALL component names from blocks to know which components are on the page
export const extractComponentNames = (content: QueryData<'page'> | null): string[] => {
	const names: string[] = [];

	const extractFromBlocks = (blocks: BuilderElement[]) => {
		if (!blocks) return;

		blocks.forEach((block) => {
			if (block.component?.name) {
				names.push(block.component.name);
			}
			if (block.children) {
				extractFromBlocks(block.children);
			}
		});
	};

	if (content?.data?.blocks) {
		extractFromBlocks(content.data.blocks);
	}

	return [...new Set(names)];
};

// Extract form IDs from the page content instead of fetching all forms
export const extractFormIds = (content: QueryData<'page'> | null): string[] => {
	const formIds: string[] = [];

	const extractFromBlocks = (blocks: BuilderElement[]) => {
		if (!blocks) return;

		blocks.forEach((block) => {
			// Check if this block is a form component
			if (block.component?.name === 'Form' || block.component?.name === 'SingleForm') {
				const formRef = block.component?.options?.form as
					| BuilderRef<{ formId: string }>
					| undefined;
				if (formRef?.value?.data?.formId) {
					formIds.push(formRef.value.data.formId);
				}
				return;
			}

			// Recursively check child blocks for non-form components
			if (block.children) {
				extractFromBlocks(block.children);
			}
		});
	};

	if (content?.data?.blocks) {
		extractFromBlocks(content.data.blocks);
	}

	return [...new Set(formIds)]; // Remove duplicates
};
